import type { LatLng } from './distance'

const TILE_SIZE = 256

/** Web Mercator world pixel coordinates at the given zoom. */
export function projectToWorldPixels(position: LatLng, zoom: number) {
  const scale = TILE_SIZE * 2 ** zoom
  const sinLat = Math.min(Math.max(Math.sin((position.lat * Math.PI) / 180), -0.9999), 0.9999)
  return {
    x: ((position.lng + 180) / 360) * scale,
    y: (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale,
  }
}

export interface ClusterInput<T> {
  id: string
  position: LatLng
  item: T
}

export interface Cluster<T> {
  id: string
  position: LatLng
  items: ClusterInput<T>[]
}

/**
 * Greedy pixel-distance clustering. Good for the tens-to-low-hundreds of markers a branch
 * board shows; items within `radiusPx` of a cluster seed at the current zoom are merged.
 */
export function clusterByPixelDistance<T>(inputs: ClusterInput<T>[], zoom: number, radiusPx = 44): Cluster<T>[] {
  const clusters: Array<Cluster<T> & { px: { x: number; y: number } }> = []

  inputs.forEach((input) => {
    const px = projectToWorldPixels(input.position, zoom)
    const target = clusters.find((cluster) => Math.hypot(cluster.px.x - px.x, cluster.px.y - px.y) <= radiusPx)
    if (target) {
      target.items.push(input)
      return
    }
    clusters.push({ id: input.id, position: input.position, items: [input], px })
  })

  return clusters.map(({ id, items }) => {
    const lat = items.reduce((sum, entry) => sum + entry.position.lat, 0) / items.length
    const lng = items.reduce((sum, entry) => sum + entry.position.lng, 0) / items.length
    return {
      id: items.length > 1 ? `cluster:${items.map((entry) => entry.id).sort().join(',')}` : id,
      position: { lat, lng },
      items,
    }
  })
}
